import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { type Experiment, GrowthBook, type Result } from '@growthbook/growthbook';

declare global {
   interface Window {
      App: {
         gb?: {
            features: Record<string, string>;
            attributes: Record<string, any>;
            forcedVariations: Record<string, number>;
            forcedFeatures: Record<string, any>;
            viewedExperiments: Array<{ experiment: Experiment<any>; result: Result<any> }>;
         };
      };
   }
}

const GROWTHBOOK_DEBUG = false;
const canGroupLogs = 'group' in console && 'groupCollapsed' in console && 'groupEnd' in console;

const logger = canGroupLogs
   ? /* eslint-disable no-console */
     (msg: string, ctx: Record<string, unknown>) => {
        console.groupCollapsed(`[GrowthBook] ${msg}`);
        console.log(ctx);
        console.groupEnd();
     }
   : (msg: string, ctx: Record<string, unknown>) => {
        console.log(`[GrowthBook] ${msg}`, ctx);
     };
/* eslint-enable no-console */

export async function handleServerExperimentViewed() {
   const growthbook = await getGrowthbook();

   if (App.gb.viewedExperiments) {
      growthbook.setDeferredTrackingCalls(App.gb.viewedExperiments);
      growthbook.fireDeferredTrackingCalls();
   }
}

export async function isFeatureOn(feature: string) {
   const growthbook = await getGrowthbook();
   return growthbook.isOn(feature);
}

let growthbookInstance: GrowthBook | null = null;

export async function getGrowthbook() {
   if (growthbookInstance) {
      return growthbookInstance;
   }

   const growthbook = new GrowthBook({
      apiHost: 'https://cdn.growthbook.io',
      clientKey: 'sdk-eWD3DuVIUI93h5o',
      enableDevMode: GROWTHBOOK_DEBUG,
      debug: GROWTHBOOK_DEBUG,
      backgroundSync: true,
      log: logger,
      trackingCallback: (experiment: Experiment<any>, result: Result<any>) => {
         const experimentId = experiment.key;
         const variationId = result.key;

         growthbook.log('Experiment Viewed', {
            experimentId,
            variationId,
            fullDetails: {
               experiment,
               result,
            },
         });

         trackInPiwikAndGA({
            eventCategory: 'Experiment Viewed',
            eventAction: variationId,
            eventName: experimentId,
         });
      },
   });

   if (App.gb?.attributes) {
      await growthbook.setAttributes(App.gb?.attributes);
   }

   if (App.gb?.features) {
      await growthbook.setPayload({
         features: App.gb.features,
      });
   }

   if (App.gb?.forcedFeatures) {
      const featureMap = new Map(Object.entries(App.gb.forcedFeatures));
      await growthbook.setForcedFeatures(featureMap);
   }

   if (App.gb?.forcedVariations) {
      await growthbook.setForcedVariations(App.gb.forcedVariations);
   }

   growthbookInstance = growthbook;

   return growthbook;
}
